import { useCallback, useEffect, useRef, useState } from "react";
import { monitoringApi } from "../../monitoring/api/monitoringApi";
import "../styles/MonitoringTab.css";

function StatusPill({ status }) {
  const cls = status === "firing" ? "mon-pill--firing" : "mon-pill--resolved";
  return (
    <span className={`mon-pill ${cls}`}>
      <span className="mon-pill--dot" />
      {status}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

const STATUS_OPTIONS = [
  { value: "firing",   label: "Firing"   },
  { value: "resolved", label: "Resolved" },
];

function MonFilterDrawer({ open, customers, custFilter, statFilter, dateFrom, dateTo, onApply, onClose }) {
  const [draftCust, setDraftCust] = useState(custFilter);
  const [draftStat, setDraftStat] = useState(statFilter);
  const [draftFrom, setDraftFrom] = useState(dateFrom);
  const [draftTo,   setDraftTo]   = useState(dateTo);
  const [expanded,  setExpanded]  = useState(null);

  useEffect(() => {
    if (!open) return;
    setDraftCust(custFilter);
    setDraftStat(statFilter);
    setDraftFrom(dateFrom);
    setDraftTo(dateTo);
    setExpanded(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const handleReset = () => { setDraftCust(""); setDraftStat(""); setDraftFrom(""); setDraftTo(""); };
  const handleApply = () => { onApply(draftCust, draftStat, draftFrom, draftTo); onClose(); };

  const radioFields = [
    {
      key: "status",
      label: "Status",
      options: STATUS_OPTIONS,
      value: draftStat,
      onChange: setDraftStat,
    },
    {
      key: "customer",
      label: "Customer",
      options: customers.map(c => ({ value: c.id, label: c.name })),
      value: draftCust,
      onChange: setDraftCust,
    },
  ];

  const hasDateActive = !!(draftFrom || draftTo);

  return (
    <>
      <div className="cmn-fdr-scrim" onClick={onClose} />
      <div className="cmn-fdr">
        <div className="cmn-fdr-header">
          <span className="cmn-fdr-title">Filter</span>
          <button className="cmn-fdr-reset" onClick={handleReset}>Reset</button>
        </div>

        <div className="cmn-fdr-list">
          {radioFields.map(field => {
            const isOpen    = expanded === field.key;
            const hasActive = !!field.value;
            return (
              <div
                key={field.key}
                className={`cmn-fdr-field${isOpen ? " cmn-fdr-field--open" : ""}${hasActive ? " cmn-fdr-field--active" : ""}`}
              >
                <button
                  className="cmn-fdr-field-hdr"
                  onClick={() => setExpanded(isOpen ? null : field.key)}
                >
                  <span className="cmn-fdr-field-name">
                    {field.label}
                    {hasActive && <span className="cmn-fdr-badge">✓</span>}
                  </span>
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    {isOpen
                      ? <polyline points="18 15 12 9 6 15" />
                      : <polyline points="9 18 15 12 9 6" />}
                  </svg>
                </button>

                {isOpen && (
                  <div className="cmn-fdr-field-body">
                    <div className="cmn-fdr-opts">
                      {field.options.length === 0 && (
                        <div className="cmn-fdr-empty">No options</div>
                      )}
                      {field.options.map(opt => (
                        <label key={opt.value} className="cmn-fdr-opt">
                          <input
                            type="radio"
                            name={`mon-fdr-${field.key}`}
                            checked={field.value === opt.value}
                            onChange={() => field.onChange(opt.value)}
                          />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                      {field.value && (
                        <label className="cmn-fdr-opt">
                          <input
                            type="radio"
                            name={`mon-fdr-${field.key}`}
                            checked={false}
                            onChange={() => field.onChange("")}
                          />
                          <span style={{ color: "#9ca3af" }}>Clear selection</span>
                        </label>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Date range field */}
          <div className={`cmn-fdr-field${expanded === "date" ? " cmn-fdr-field--open" : ""}${hasDateActive ? " cmn-fdr-field--active" : ""}`}>
            <button
              className="cmn-fdr-field-hdr"
              onClick={() => setExpanded(expanded === "date" ? null : "date")}
            >
              <span className="cmn-fdr-field-name">
                Last Seen Date
                {hasDateActive && <span className="cmn-fdr-badge">✓</span>}
              </span>
              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                {expanded === "date"
                  ? <polyline points="18 15 12 9 6 15" />
                  : <polyline points="9 18 15 12 9 6" />}
              </svg>
            </button>
            {expanded === "date" && (
              <div className="cmn-fdr-field-body">
                <div className="cmn-fdr-daterange">
                  <span className="cmn-fdr-daterange-label">From</span>
                  <input
                    type="datetime-local"
                    className="cmn-fdr-date-input"
                    value={draftFrom}
                    max={draftTo || undefined}
                    onChange={e => setDraftFrom(e.target.value)}
                  />
                  <span className="cmn-fdr-daterange-label">To</span>
                  <input
                    type="datetime-local"
                    className="cmn-fdr-date-input"
                    value={draftTo}
                    min={draftFrom || undefined}
                    onChange={e => setDraftTo(e.target.value)}
                  />
                </div>
                {hasDateActive && (
                  <button
                    className="cmn-fdr-field-reset"
                    onClick={() => { setDraftFrom(""); setDraftTo(""); }}
                  >
                    Clear dates
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="cmn-fdr-footer">
          <button className="cmn-fdr-cancel" onClick={onClose}>Cancel</button>
          <button className="cmn-fdr-find"   onClick={handleApply}>Apply</button>
        </div>
      </div>
    </>
  );
}

function BellIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

function AlertDetailDrawer({ alert, onClose }) {
  if (!alert) return null;

  const Field = ({ label, children }) => (
    <div className="mon-adr-field">
      <span className="mon-adr-label">{label}</span>
      <span className="mon-adr-value">{children}</span>
    </div>
  );

  return (
    <>
      <div className="mon-adr-scrim" onClick={onClose} />
      <div className="mon-adr">
        <div className="mon-adr-header">
          <div className="mon-adr-title-row">
            <span className="mon-adr-title">{alert.alert_name || "Alert"}</span>
            <StatusPill status={alert.status} />
          </div>
          <button className="mon-adr-close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="mon-adr-body">
          <div className="mon-adr-section">
            <span className="mon-adr-section-label">Incident</span>
            <Field label="Customer">{alert.customer_name || <span className="mon-adr-empty">—</span>}</Field>
            <Field label="Fingerprint">
              <span className="mon-adr-mono">{alert.fingerprint || <span className="mon-adr-empty">—</span>}</span>
            </Field>
            <Field label="Last Seen">
              <span className="mon-adr-mono">{fmtDate(alert.created_at)}</span>
            </Field>
          </div>

          <div className="mon-adr-section">
            <span className="mon-adr-section-label">Activity (24 h)</span>
            <div className="mon-adr-counts">
              <div className="mon-adr-count-card mon-adr-count-card--fired">
                <span className="mon-adr-count-num">{alert.fired_24h ?? 0}</span>
                <span className="mon-adr-count-lbl">Fired</span>
              </div>
              <div className="mon-adr-count-card mon-adr-count-card--resolved">
                <span className="mon-adr-count-num">{alert.resolved_24h ?? 0}</span>
                <span className="mon-adr-count-lbl">Resolved</span>
              </div>
            </div>
          </div>

          {(alert.summary || alert.description) && (
            <div className="mon-adr-section">
              <span className="mon-adr-section-label">Details</span>
              {alert.summary && (
                <div className="mon-adr-field mon-adr-field--block">
                  <span className="mon-adr-label">Summary</span>
                  <p className="mon-adr-text">{alert.summary}</p>
                </div>
              )}
              {alert.description && (
                <div className="mon-adr-field mon-adr-field--block">
                  <span className="mon-adr-label">Description</span>
                  <p className="mon-adr-text">{alert.description}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function CustomerAlertGrid({ byCustomer, customers, selectedCustId, onCustomerClick }) {
  const tileMap = {};
  byCustomer.forEach(({ customer_name, status, count }) => {
    if (!customer_name) return;
    if (!tileMap[customer_name]) tileMap[customer_name] = { name: customer_name, firing: 0, resolved: 0 };
    if (status === "firing")   tileMap[customer_name].firing   = Number(count);
    if (status === "resolved") tileMap[customer_name].resolved = Number(count);
  });

  const tiles = Object.values(tileMap).map(t => ({
    ...t,
    id: customers.find(c => c.name === t.name)?.id || null,
  }));

  tiles.sort((a, b) => b.firing - a.firing || a.name.localeCompare(b.name));

  if (tiles.length === 0) return null;

  return (
    <div className="mon-cust-grid">
      {tiles.map(tile => {
        const isFiring   = tile.firing > 0;
        const isSelected = selectedCustId && selectedCustId === tile.id;
        return (
          <div
            key={tile.name}
            className={`mon-cust-tile ${isFiring ? "mon-cust-tile--firing" : "mon-cust-tile--ok"}${isSelected ? " mon-cust-tile--selected" : ""}`}
            onClick={() => onCustomerClick?.(isSelected ? "" : tile.id)}
            role="button"
            tabIndex={0}
            onKeyDown={e => e.key === "Enter" && onCustomerClick?.(isSelected ? "" : tile.id)}
          >
            <div className="mon-cust-tile__header">
              <span className="mon-cust-tile__name">{tile.name}</span>
              <span className={`mon-cust-tile__badge ${isFiring ? "mon-cust-tile__badge--firing" : "mon-cust-tile__badge--ok"}`}>
                {isFiring ? "FIRING" : "OK"}
              </span>
            </div>
            <div className="mon-cust-tile__body">
              <div className="mon-cust-tile__counts">
                <span className="mon-cust-tile__count-label">FIRING ALERTS</span>
                <span className="mon-cust-tile__count">{tile.firing}</span>
                <span className="mon-cust-tile__resolved">{tile.resolved} resolved</span>
              </div>
              <div className="mon-cust-tile__bell">
                <BellIcon />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function MonitoringTab({ user, search = "", custFilter = "", statFilter = "", dateFrom = "", dateTo = "", drawerOpen = false, refreshInterval = 0, onDrawerClose, onFilterApply, onCustomerClick }) {
  const [alerts,        setAlerts]        = useState([]);
  const [stats,         setStats]         = useState({ firing: 0, resolved: 0, total: 0 });
  const [byCustomer,    setByCustomer]    = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [customers,     setCustomers]     = useState([]);
  const [selectedAlert, setSelectedAlert] = useState(null);

  const debounceRef      = useRef(null);
  const mountedRef       = useRef(false);
  const currentParamsRef = useRef({});
  const intervalRef      = useRef(null);

  const load = useCallback(async (params = {}, silent = false) => {
    if (!silent) setLoading(true);
    try {
      // fullStats always uses no filters so the customer grid always shows all customers
      const [alertData, statsData, fullStats] = await Promise.all([
        monitoringApi.listAlerts({ limit: 200, ...params }),
        monitoringApi.alertStats(params.customer_id ? { customer_id: params.customer_id } : {}),
        monitoringApi.alertStats({}),
      ]);
      const list     = Array.isArray(alertData) ? alertData : [];
      const byStatus = Array.isArray(statsData?.by_status) ? statsData.by_status : [];
      setAlerts(list);

      const firing   = byStatus.find(s => s.status === "firing")?.count   || 0;
      const resolved = byStatus.find(s => s.status === "resolved")?.count || 0;
      setStats({ firing: Number(firing), resolved: Number(resolved), total: Number(firing) + Number(resolved) });

      setByCustomer(Array.isArray(fullStats?.by_customer) ? fullStats.by_customer : []);

      const seen = new Map();
      list.forEach(a => {
        if (a.customer_id && a.customer_name) seen.set(a.customer_id, a.customer_name);
      });
      setCustomers([...seen.entries()].map(([id, name]) => ({ id, name })));
    } catch {
      // keep prior state
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load — backfill first so any NULL customer_id rows are resolved
  useEffect(() => {
    monitoringApi.backfillMappings().catch(() => {}).finally(() => load());
  }, [load]);

  // Debounced reload when filters change (skip the initial mount fire)
  useEffect(() => {
    if (!mountedRef.current) { mountedRef.current = true; return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const params = {};
      if (custFilter)    params.customer_id = custFilter;
      if (statFilter)    params.status      = statFilter;
      if (search.trim()) params.alert_name  = search.trim();
      if (dateFrom)      params.date_from   = dateFrom;
      if (dateTo)        params.date_to     = dateTo;
      currentParamsRef.current = params;
      load(params);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [search, custFilter, statFilter, dateFrom, dateTo, load]);

  // Auto-refresh interval
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;
    if (!refreshInterval) return;
    intervalRef.current = setInterval(() => {
      load(currentParamsRef.current, true);
    }, refreshInterval);
    return () => { clearInterval(intervalRef.current); intervalRef.current = null; };
  }, [refreshInterval, load]);

  return (
    <div className="mon-wrap">
      {/* Stat cards */}
      <div className="mon-stats">
        <div className="mon-stat-card mon-stat-card--firing">
          <span className="mon-stat-card__label">Firing</span>
          <span className="mon-stat-card__value">{stats.firing}</span>
        </div>
        <div className="mon-stat-card mon-stat-card--resolved">
          <span className="mon-stat-card__label">Resolved</span>
          <span className="mon-stat-card__value">{stats.resolved}</span>
        </div>
        <div className="mon-stat-card mon-stat-card--total">
          <span className="mon-stat-card__label">Total</span>
          <span className="mon-stat-card__value">{stats.total}</span>
        </div>
      </div>

      {/* Customer alert grid */}
      <CustomerAlertGrid
        byCustomer={byCustomer}
        customers={customers}
        selectedCustId={custFilter}
        onCustomerClick={(id) => onCustomerClick?.(id)}
      />

      {/* Alert detail drawer */}
      <AlertDetailDrawer alert={selectedAlert} onClose={() => setSelectedAlert(null)} />

      {/* Filter drawer */}
      <MonFilterDrawer
        open={drawerOpen}
        customers={customers}
        custFilter={custFilter}
        statFilter={statFilter}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onApply={(c, s, f, t) => onFilterApply?.(c, s, f, t)}
        onClose={() => onDrawerClose?.()}
      />

      {/* Alerts table */}
      <div className="mon-table-wrap">
        <table className="mon-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Alert</th>
              <th>Status</th>
              <th>Fingerprint</th>
              <th className="mon-th--count" title="Times fired in last 24 hours">Fired (24h)</th>
              <th className="mon-th--count" title="Times resolved in last 24 hours">Resolved (24h)</th>
              <th>Last seen</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="mon-empty">Loading…</td></tr>
            )}
            {!loading && alerts.length === 0 && (
              <tr><td colSpan={7} className="mon-empty">No alerts found.</td></tr>
            )}
            {!loading && [...alerts]
              .sort((a, b) => (a.status === "firing" ? -1 : 1) - (b.status === "firing" ? -1 : 1))
              .map(a => (
              <tr
                key={a.id}
                className={`mon-tr--clickable${selectedAlert?.id === a.id ? " mon-tr--selected" : ""}`}
                onClick={() => setSelectedAlert(prev => prev?.id === a.id ? null : a)}
              >
                <td className="mon-td--customer">{a.customer_name || <span style={{ color: "#9ca3af" }}>—</span>}</td>
                <td className="mon-td--name">{a.alert_name || "—"}</td>
                <td><StatusPill status={a.status} /></td>
                <td className="mon-td--fingerprint">{a.fingerprint || "—"}</td>
                <td className="mon-td--count">
                  {a.fired_24h > 0
                    ? <span className="mon-count mon-count--fired">{a.fired_24h}</span>
                    : <span className="mon-count mon-count--zero">0</span>}
                </td>
                <td className="mon-td--count">
                  {a.resolved_24h > 0
                    ? <span className="mon-count mon-count--resolved">{a.resolved_24h}</span>
                    : <span className="mon-count mon-count--zero">0</span>}
                </td>
                <td className="mon-td--date">{fmtDate(a.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
